import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef
} from 'react';
import {
  Link,
  Redirect,
  Route,
  Switch,
  useLocation,
  useParams
} from 'react-router-dom';
import styled from 'styled-components';
import { NumberParam, useQueryParam } from 'use-query-params';
import { Button, Tab, Tabs } from '@material-ui/core';

import { FetchError } from '../../common/api/api';
import AppSnackbar from '../../common/AppSnackbar';
import { formatDate } from '../../common/formatter';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import PageTemplate from '../../common/page-template/PageTemplate';
import PageTemplateNav from '../../common/page-template/PageTemplateNav';
import PageTemplateHeader, {
  PageTemplateHeaderActions
} from '../../common/page-template/PageTemplateHeader';
import PageTemplateAlert from '../../common/page-template/PageTemplateAlert';
import PageTemplateDashboard from '../../common/page-template/PageTemplateDashboard';
import TaskStatusActions from '../tasks/TaskStatusActions';
import SupplierTabInfo from './tabs/SupplierTabInfo';
import PurchaseOrderTab from './tabs/PurchaseOrderTab';
import SupplierCorrectiveActionTabInfo from './tabs/SupplierCorrectiveActionTabInfo';
import {
  Supplier,
  BaseSupplierCardProps,
  SupplierVersion
} from './Supplier.interface';
import {
  useFindSupplierByTask,
  useFindSupplierVersion,
  useUpdateSupplier
} from './Supplier.hooks';

const StyledTitle = styled.div`
  display: flex;
  flex-direction: column;
  .status-container {
    margin-top: 5px;
  }
`;

export const SupplierDetailPage: React.FC = () => {
  const { pathname } = useLocation();
  const { taskId: paramTaskId = '' } = useParams<{ taskId?: string }>();
  const taskId: number = parseInt(paramTaskId) || 0;
  const [version] = useQueryParam('ver', NumberParam);
  const addCallbackRef = useRef<(() => void) | undefined>();
  const [supplier, setSupplier] = useState<Supplier>();
  const [supplierVersion, setSupplierVersion] = useState<SupplierVersion>();
  const [error, setError] = useState<FetchError | undefined>();
  const [didUpdate, setDidUpdate] = useState<boolean>(false);
  const { supplier: loadedSupplier, loadingError } = useFindSupplierByTask(
    taskId,
    true
  );
  const {
    supplierVersion: loadedSupplierVersion,
    loadingError: findVersionError,
    findSupplierVersion
  } = useFindSupplierVersion(supplier?.id || 0, version || 0);
  const { updatedSupplier, updateError, updateSupplier } = useUpdateSupplier(
    supplier?.id || 0
  );
  const tabValue: string = useMemo(() => {
    return pathname.slice(pathname.lastIndexOf('/') + 1, pathname.length);
  }, [pathname]);

  const supplierCardProps: BaseSupplierCardProps = {
    supplier: supplier!,
    supplierVersion,
    onChange: (newSupplier: Partial<Supplier>) => {
      supplier && setSupplier({ ...supplier, ...newSupplier });
    },
    onUpdate: () => setDidUpdate(true),
    onError: (error: FetchError) => setError(error)
  };

  const handleAddItem = useCallback((callback?: () => void) => {
    addCallbackRef.current = callback;
  }, []);

  useEffect(() => {
    if (version) {
      findSupplierVersion();
    } else {
      setSupplierVersion(undefined);
    }
  }, [version, findSupplierVersion]);

  useEffect(
    () => loadedSupplier && setSupplier(loadedSupplier),
    [loadedSupplier]
  );

  useEffect(() => {
    loadedSupplierVersion && setSupplierVersion(loadedSupplierVersion);
  }, [loadedSupplierVersion]);

  useEffect(() => {
    if (updatedSupplier) {
      supplierCardProps.onChange?.(updatedSupplier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedSupplier]);

  useEffect(() => {
    setError(loadingError || findVersionError);
  }, [loadingError, findVersionError]);

  useEffect(() => {
    setError(updateError);
  }, [updateError]);

  if (!['info', 'scar', 'purchase'].includes(tabValue)) {
    // if navigating to /portal/supplier/task/:taskId redirect to edit tab
    return <Redirect to={`/portal/suppliers/task/${taskId}/info`} />;
  }

  return (
    <PageTemplate>
      <PageTemplateHeader>
        <StyledTitle>
          <h2>
            {supplier && 'SUPPLIER-' + supplier.id} {supplier?.companyName}
          </h2>
          <div className='status-container'>
            {supplier && <TaskStatusLabel task={supplier.task} />}
          </div>
        </StyledTitle>
        <PageTemplateHeaderActions flex>
          {tabValue === 'info' && supplier?.task && (
            <TaskStatusActions
              task={supplier.task}
              taskEntity={supplier}
              onChange={task => {
                supplier.task = task;
                setSupplier({ ...supplier });
                // update supplier next review date
                const { nextReviewDate } = supplier;
                nextReviewDate && updateSupplier({ nextReviewDate });
              }}
            />
          )}
          <Route exact path='/portal/suppliers/task/:taskId/scar'>
            <Button
              color='primary'
              variant='contained'
              onClick={() => addCallbackRef.current?.()}
            >
              Add SCAR
            </Button>
          </Route>
          <Route exact path='/portal/suppliers/task/:taskId/purchase'>
            <Button
              color='primary'
              variant='contained'
              onClick={() => addCallbackRef.current?.()}
            >
              Add Purchase Order
            </Button>
          </Route>
        </PageTemplateHeaderActions>
      </PageTemplateHeader>
      {supplier && version && (
        <PageTemplateAlert severity='warning'>
          Viewing previous version {version}
          {supplierVersion && ` from ${formatDate(supplierVersion.created)}. `}
          <Link to={`/portal/suppliers/task/${supplier.task.id}`}>
            Click here to view latest version
          </Link>
        </PageTemplateAlert>
      )}
      <PageTemplateNav>
        <Tabs
          value={tabValue}
          variant='scrollable'
          indicatorColor='primary'
          scrollButtons='auto'
        >
          <Tab
            component={Link}
            label='Supplier Info'
            value='info'
            to='./info'
          />
          <Tab component={Link} label='SCAR' value='scar' to='./scar' />
          <Tab
            component={Link}
            label='Purchase Order'
            value='purchase'
            to='./purchase'
          />
        </Tabs>
      </PageTemplateNav>
      <PageTemplateDashboard>
        <Switch>
          <Route exact path='/portal/suppliers/task/:taskId/info'>
            {supplier && <SupplierTabInfo {...supplierCardProps} />}
          </Route>
          <Route exact path='/portal/suppliers/task/:taskId/scar'>
            {supplier && (
              <SupplierCorrectiveActionTabInfo
                supplier={supplier}
                handleAddItem={handleAddItem}
              />
            )}
          </Route>
          <Route exact path='/portal/suppliers/task/:taskId/purchase'>
            {supplier && (
              <PurchaseOrderTab
                supplier={supplier}
                handleAddItem={handleAddItem}
              />
            )}
          </Route>
        </Switch>
      </PageTemplateDashboard>

      <AppSnackbar
        severity='error'
        open={!!error}
        onClose={() => setError(undefined)}
      >
        {error?.message || 'An error occurred while loading Supplier details.'}
      </AppSnackbar>
      <AppSnackbar
        severity='success'
        open={didUpdate}
        onClose={() => didUpdate && setDidUpdate(false)}
      >
        Supplier details updated.
      </AppSnackbar>
    </PageTemplate>
  );
};

export default SupplierDetailPage;
